import React, { useState, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Button,
  TextField,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  WarningAmberRounded as WarningIcon,
  EditNote as NoteIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { saveNurseNotedb } from '../BaseURL';

const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const WARN         = '#b45309';
const WARN_BG      = 'rgba(234,179,8,0.10)';
const WARN_BORDER  = 'rgba(234,179,8,0.35)';
const DANGER       = '#b91c1c';
const DANGER_BG    = 'rgba(220,38,38,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

// Backend compliance rule: 1 nursing note per admission day (vitals stays
// at 3, see ComplianceVitalsBackfill). The backfill UI keeps the "+ New"
// path so a nurse can still add additional notes if they want to, but
// the day is considered compliant the moment the first note exists.
const MIN_PER_DAY     = 1;
const MIN_NOTE_LENGTH = 5;
const MAX_NOTE_LENGTH = 4000;

const eachDateBetween = (startStr, endStr) => {
  if (!startStr) return [];
  const out = [];
  const start = new Date(`${startStr}T00:00:00`);
  const end   = new Date(`${(endStr || startStr)}T00:00:00`);
  if (isNaN(start) || isNaN(end)) return out;
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const todayYMD = () => moment().format('YYYY-MM-DD');

const validateNote = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed)                            return { valid: false, message: 'Note cannot be empty' };
  if (trimmed.length < MIN_NOTE_LENGTH)    return { valid: false, message: `At least ${MIN_NOTE_LENGTH} characters` };
  if (trimmed.length > MAX_NOTE_LENGTH)    return { valid: false, message: `Maximum ${MAX_NOTE_LENGTH} characters` };
  return { valid: true, message: '' };
};

/**
 * Backfill table for past-day nursing notes. Sits below the existing
 * "Add Nursing Note" card on the Nurses Note tab so nurses can add the
 * missing entries for any admission day that's below the compliance
 * minimum — without changing the date.
 *
 * Uses the /saveNurseNote endpoint with mode='append', which bypasses
 * the default one-row-per-nurse-per-day upsert so the same nurse can
 * record several notes on the same past date when needed.
 *
 * Props:
 *   patient        { id, fullName, age, gender }
 *   admissionDate  YYYY-MM-DD
 *   currentUser    nurse username
 *   notesHistory   array of notes already loaded by the parent
 *                  (each: { trans_code, dat, time, note, user })
 *   onSaved        callback fired after a successful save
 */
const ComplianceNotesBackfill = ({
  patient,
  admissionDate,
  currentUser,
  notesHistory = [],
  onSaved,
}) => {
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [showEntryFor, setShowEntryFor] = useState(null);
  const [draft, setDraft]               = useState('');
  const [saving, setSaving]             = useState(false);

  // Group notes by their `dat` value.
  const byDate = useMemo(() => {
    const map = {};
    for (const n of notesHistory) {
      const d = n.dat;
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(n);
    }
    return map;
  }, [notesHistory]);

  // Reverse-chronological dates from admission → today.
  const dates = useMemo(() => {
    return eachDateBetween(admissionDate, todayYMD()).reverse();
  }, [admissionDate]);

  if (!patient || !admissionDate || dates.length === 0) return null;

  const toggleExpand = (date) => {
    setExpandedDate(prev => (prev === date ? null : date));
    setShowEntryFor(null);
    setDraft('');
  };

  const startEntry = (date) => {
    setShowEntryFor(date);
    setDraft('');
  };

  const cancelEntry = () => {
    setShowEntryFor(null);
    setDraft('');
  };

  const handleSave = async (date) => {
    const { valid, message } = validateNote(draft);
    if (!valid) {
      Swal.fire({ icon: 'warning', title: 'Note not saved', text: message });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(saveNurseNotedb, {
        patientId: patient.id,
        note: draft.trim(),
        user: currentUser,
        name: patient.fullName,
        age: patient.age,
        gen: patient.gender,
        dat: date,
        time: moment().format('HH:mm:ss'),
        mode: 'append',  // never upsert from the backfill UI
      }, { timeout: 15000 });

      if (res.data?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Note saved',
          text: `Recorded for ${date}.`,
          timer: 1400, showConfirmButton: false,
        });
        setDraft('');
        setShowEntryFor(null);
        onSaved?.();
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res.data?.message || 'Failed to save note' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || 'Failed to save note' });
    } finally {
      setSaving(false);
    }
  };

  const incompleteCount = dates.reduce((acc, d) => {
    const c = (byDate[d] || []).length;
    return acc + (c < MIN_PER_DAY ? 1 : 0);
  }, 0);

  const closeDialog = () => {
    setDialogOpen(false);
    setExpandedDate(null);
    setShowEntryFor(null);
    setDraft('');
  };

  return (
    <>
      {/* Trigger button — sits next to the Save Note button on the parent's
          Nurses Note tab. Red badge surfaces the number of admission days
          currently below the per-day minimum so the nurse knows at a glance
          there's something outstanding. */}
      <Tooltip
        title={incompleteCount > 0
          ? `${incompleteCount} day${incompleteCount === 1 ? '' : 's'} below the ${MIN_PER_DAY}-note minimum`
          : `All admission days meet the ${MIN_PER_DAY}-note minimum`}
        arrow
      >
        <Badge
          badgeContent={incompleteCount > 0 ? incompleteCount : 0}
          color="error"
          max={99}
          invisible={incompleteCount === 0}
          overlap="circular"
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={incompleteCount > 0
              ? <WarningIcon sx={{ fontSize: 16 }} />
              : <CheckIcon sx={{ fontSize: 16 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              borderColor: incompleteCount > 0 ? WARN_BORDER : 'rgba(65,163,134,0.4)',
              color: incompleteCount > 0 ? WARN : PRIMARY_DARK,
              bgcolor: incompleteCount > 0 ? WARN_BG : 'rgba(65,163,134,0.06)',
              '&:hover': {
                borderColor: incompleteCount > 0 ? WARN : PRIMARY,
                bgcolor: incompleteCount > 0 ? 'rgba(234,179,8,0.18)' : 'rgba(65,163,134,0.12)',
              },
            }}
          >
            Backfill Notes
          </Button>
        </Badge>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, py: 1.25,
          bgcolor: incompleteCount > 0 ? WARN_BG : PRIMARY_BG,
          color: incompleteCount > 0 ? WARN : PRIMARY_DARK,
          borderBottom: `1px solid ${BORDER}`,
        }}>
          {incompleteCount > 0
            ? <WarningIcon sx={{ fontSize: 20 }} />
            : <CheckIcon sx={{ fontSize: 20 }} />}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.8px' }}>
              {MIN_PER_DAY} NURSING NOTES REQUIRED PER ADMISSION DAY
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
              {patient.fullName || patient.id} · Backfill Nursing Notes
            </Typography>
          </Box>
          <Chip
            size="small"
            label={incompleteCount > 0
              ? `${incompleteCount} day${incompleteCount === 1 ? '' : 's'} below minimum`
              : 'All days compliant'}
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: '#fff',
              color: incompleteCount > 0 ? WARN : PRIMARY_DARK,
              border: `1px solid ${incompleteCount > 0 ? WARN_BORDER : 'rgba(65,163,134,0.3)'}`,
            }}
          />
          <IconButton size="small" onClick={closeDialog} sx={{ color: 'inherit', ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {/* Per-day rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {dates.map(date => {
          const entries = byDate[date] || [];
          const count = entries.length;
          const complete = count >= MIN_PER_DAY;
          const isExpanded = expandedDate === date;
          const isToday = date === todayYMD();
          const isEntryOpen = showEntryFor === date;

          return (
            <Paper
              key={date}
              variant="outlined"
              sx={{
                borderColor: complete ? BORDER : 'rgba(220,38,38,0.25)',
                bgcolor: '#fff',
                overflow: 'hidden',
              }}
            >
              {/* Row header */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.25, py: 0.85,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
                onClick={() => toggleExpand(date)}
              >
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG, minWidth: 100 }}>
                  {date}
                  {isToday && (
                    <Box component="span" sx={{ ml: 0.75, fontSize: '0.65rem', fontWeight: 700, color: PRIMARY_DARK }}>
                      · TODAY
                    </Box>
                  )}
                </Typography>
                <Chip
                  size="small"
                  icon={<NoteIcon sx={{ fontSize: 12 }} />}
                  label={`${count}/${MIN_PER_DAY}`}
                  sx={{
                    height: 22, fontSize: '0.72rem', fontWeight: 700,
                    bgcolor: complete ? PRIMARY_BG : DANGER_BG,
                    color: complete ? PRIMARY_DARK : DANGER,
                    border: `1px solid ${complete ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                  }}
                />
                {!complete && (
                  <Typography sx={{ fontSize: '0.72rem', color: DANGER, fontStyle: 'italic' }}>
                    {MIN_PER_DAY - count} more required
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" sx={{ color: MUTED }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>

              <Collapse in={isExpanded} unmountOnExit>
                <Divider />
                <Box sx={{ px: 1.25, py: 1 }}>
                  {/* Existing notes */}
                  {entries.length > 0 && (
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                      <Typography sx={{ ...LABEL_SX, mb: 0.25 }}>Recorded notes</Typography>
                      {entries.map((n, idx) => (
                        <Box
                          key={n.trans_code || idx}
                          sx={{
                            display: 'flex', flexDirection: 'column', gap: 0.5,
                            px: 1, py: 0.6,
                            borderRadius: 0.75,
                            bgcolor: '#f8fafc',
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: STRONG, minWidth: 56 }}>
                              #{idx + 1}
                            </Typography>
                            {n.time && (
                              <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                                {String(n.time).substring(0, 5)}
                              </Typography>
                            )}
                            {n.user && (
                              <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontStyle: 'italic', ml: 'auto' }}>
                                by {n.user}
                              </Typography>
                            )}
                          </Box>
                          <Typography sx={{
                            fontSize: '0.78rem', color: STRONG,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {n.note}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {/* New entry trigger + form */}
                  {!isEntryOpen ? (
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => startEntry(date)}
                      disabled={complete}
                      sx={{
                        textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                        px: 1.5, borderRadius: 1,
                        bgcolor: complete ? '#f1f5f9' : PRIMARY,
                        color: complete ? MUTED : '#fff',
                        '&:hover': {
                          bgcolor: complete ? '#f1f5f9' : PRIMARY_DARK,
                        },
                        '&.Mui-disabled': { bgcolor: '#f1f5f9', color: MUTED },
                      }}
                    >
                      {complete ? `Day already has ${MIN_PER_DAY}` : `New note for ${date}`}
                    </Button>
                  ) : (
                    <NoteEntryForm
                      draft={draft}
                      onChange={setDraft}
                      onCancel={cancelEntry}
                      onSave={() => handleSave(date)}
                      saving={saving}
                      date={date}
                    />
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Button
            onClick={closeDialog}
            sx={{ textTransform: 'none', fontSize: '0.82rem', color: MUTED }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NoteEntryForm = ({ draft, onChange, onCancel, onSave, saving, date }) => {
  const { valid, message } = validateNote(draft);
  const charCount = String(draft || '').length;

  return (
    <Box sx={{
      p: 1,
      borderRadius: 1,
      bgcolor: PRIMARY_BG,
      border: `1px solid rgba(65,163,134,0.3)`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography sx={{ ...LABEL_SX, color: PRIMARY_DARK }}>
          NEW NOTE · {date}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Cancel">
          <IconButton size="small" onClick={onCancel} disabled={saving} sx={{ color: MUTED, p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
        placeholder="Describe the patient's status, response to care, observations…"
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        disabled={saving}
        error={!!draft && !valid}
        helperText={
          draft && !valid
            ? message
            : `Min ${MIN_NOTE_LENGTH} chars · ${charCount}/${MAX_NOTE_LENGTH}`
        }
        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
        FormHelperTextProps={{ sx: { fontSize: '0.64rem', mx: 0.5, mt: 0.25 } }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            fontSize: '0.82rem',
            py: 0.5,           // tighter vertical padding inside the textarea
          },
          '& .MuiOutlinedInput-input': { py: 0.5 },
        }}
      />

      <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
        <Button
          size="small"
          onClick={onCancel}
          disabled={saving}
          sx={{ textTransform: 'none', fontSize: '0.76rem', color: MUTED, py: 0.25 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
          onClick={onSave}
          disabled={saving || !valid}
          sx={{
            textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
            bgcolor: PRIMARY, color: '#fff', boxShadow: 'none',
            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
          }}
        >
          {saving ? 'Saving…' : `Save for ${date}`}
        </Button>
      </Box>
    </Box>
  );
};

export default ComplianceNotesBackfill;
